import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, apiFetch } from "../utils/api";
import "../styles/InvitePage.css";

const logo = "/images/Logo.svg";

const InvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [preview, setPreview] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!token) {
        setError("Der Einladungslink enthält keinen Token.");
        setLoading(false);
        return;
      }

      try {
        const invitePreview = await apiFetch(`/api/connections/invite-links/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setPreview(invitePreview);

        const sessionToken = localStorage.getItem("token");
        if (sessionToken) {
          const response = await fetch(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
          });
          if (response.ok) {
            const currentUser = await response.json();
            if (!cancelled) setUser(currentUser);
          } else if (response.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("accountType");
          }
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const authPath = (mode) => `/?invite=${encodeURIComponent(token)}&mode=${mode}`;

  const acceptInvitation = async () => {
    setAccepting(true);
    setError("");
    try {
      const result = await apiFetch(`/api/connections/invite-links/${encodeURIComponent(token)}/accept`, {
        method: "POST",
      });
      setSuccess(result.message);
    } catch (acceptError) {
      setError(acceptError.message);
    } finally {
      setAccepting(false);
    }
  };

  const invitationTitle = preview?.kind === "unternehmen"
    ? `${preview.name} lädt Sie ein`
    : `${preview?.name || "Eine Bedienung"} möchte sich verbinden`;
  const invitationText = preview?.kind === "unternehmen"
    ? "Nach Ihrer Bestätigung werden Sie als Bedienung mit diesem Unternehmen verbunden."
    : "Nach Ihrer Bestätigung werden die beiden Bedienungsaccounts miteinander verbunden.";
  const acceptLabel = preview?.kind === "unternehmen" ? "Unternehmen beitreten" : "Verbindung bestätigen";

  return (
    <main className="invite-page">
      <section className="invite-card">
        <img src={logo} alt="Festzeltfinanzen Logo" className="invite-logo" />
        {loading && <p>Einladung wird geladen …</p>}

        {!loading && error && !preview && (
          <div className="invite-state error" role="alert">
            <h1>Einladung nicht verfügbar</h1>
            <p>{error}</p>
          </div>
        )}

        {!loading && preview && (
          <>
            <p className="invite-eyebrow">Einladung über Festzeltfinanzen</p>
            <h1>{invitationTitle}</h1>
            <p>{invitationText}</p>
            <p className="invite-expiry">
              Gültig bis {new Date(preview.expiresAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })} Uhr
            </p>

            {success ? (
              <div className="invite-state success" role="status">
                <h2>Erfolgreich</h2>
                <p>{success}</p>
                <button type="button" onClick={() => navigate("/dashboard?section=connections")}>Zu den Verbindungen</button>
              </div>
            ) : user?.accountType === "bedienung" ? (
              <div className="invite-actions">
                {error && <p className="invite-error" role="alert">{error}</p>}
                <p>Angemeldet als {user.first_name} {user.last_name}</p>
                <button type="button" onClick={acceptInvitation} disabled={accepting}>
                  {accepting ? "Wird bestätigt …" : acceptLabel}
                </button>
              </div>
            ) : (
              <div className="invite-actions">
                {user && <p>Für diese Einladung benötigen Sie einen Bedienungsaccount.</p>}
                <button type="button" onClick={() => navigate(authPath("login"))}>Als Bedienung anmelden</button>
                <button type="button" className="secondary" onClick={() => navigate(authPath("register"))}>
                  Bedienungsaccount registrieren
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default InvitePage;
